import { useRef, useState } from "react";
import { Card, Button, Row, Col, InputGroup, Form } from "react-bootstrap";
import {
  ExternalLink,
  Copy,
  Plus,
  RefreshCw,
  Download,
  Edit,
  Check,
} from "lucide-react";
import { FRONTEND_URL } from "../config";
import { PLACE_HOLDER_IMAGE } from "../constants";
import { message } from "antd";
import Loader from "./Loader";

const ValetLocationQrCode = ({
  location = {},
  onUpload,
  uploading,
  openQRCodeModal,
  onSave,
}) => {
  const fileInputRef = useRef(null);
  const [editing, setEditing] = useState(false);

  const { locationName, address, qrCode, botRef } = location;

  const buildUrl = () => {
    const url = new URL(`${FRONTEND_URL}/bot`);
    if (botRef) url.searchParams.set("ref", botRef);
    return url.toString();
  };

  const handleOpen = () => {
    if (!botRef) return message.warning("No botRef found");
    window.open(buildUrl(), "_blank", "noopener,noreferrer");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildUrl());
      message.success("Link copied!");
    } catch {
      message.error("Failed to copy link");
    }
  };

  const triggerUpload = () => fileInputRef.current?.click();

  const handleFileChange = (e) => onUpload && onUpload(e, location.botRefId);

  const handleSave = () => {
    onSave && onSave();
    setEditing(false);
  };

  return (
    <Card className={`transition-card ${editing ? "editing" : ""}`}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0 text-primary">{locationName}</Card.Title>
        {editing ? (
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setEditing(false)}
            >
              Close
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={handleSave}
              className="d-flex align-items-center gap-1"
            >
              <Check size={16} /> Save
            </Button>
          </div>
        ) : (
          <Button
            variant="outline-primary"
            className="border-0"
            size="sm"
            onClick={() => setEditing(true)}
          >
            <Edit size={16} />
          </Button>
        )}
      </Card.Header>

      <Card.Body>
        <Row className="g-4 align-items-center">
          {/* Left Side */}
          <Col lg={8}>
            {address && (
              <div className="mb-3 border rounded p-2">
                <span className="text-primary">Address: </span>
                <span className="text-muted fw-light">{address}</span>
              </div>
            )}

            <div>
              {editing && (
                <Form.Label className="text-secondary mb-2">
                  Chatbot URL
                </Form.Label>
              )}
              <InputGroup>
                <Form.Control
                  type="text"
                  value={buildUrl()}
                  disabled
                  className="form-control bg-white text-secondary fw-light"
                />
                <Button
                  variant="outline-secondary border-1 border"
                  onClick={handleOpen}
                >
                  <ExternalLink size={16} />
                </Button>
                <Button
                  variant="outline-secondary border-1 border"
                  onClick={handleCopy}
                >
                  <Copy size={16} />
                </Button>
              </InputGroup>
            </div>
          </Col>

          {/* Right Side QR */}
          <Col md={4} className="d-flex justify-content-center">
            <div className="text-center">
              <img
                src={qrCode || PLACE_HOLDER_IMAGE}
                alt={locationName}
                style={{ width: 170, height: 170 }}
                className="d-block mx-auto"
              />

              {editing && (
                <div className="mt-3 d-flex justify-content-center gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={() => openQRCodeModal(location, true)}
                    className="d-flex align-items-center gap-1"
                  >
                    <Plus size={16} /> Create
                  </Button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={handleFileChange}
                  />

                  <Button
                    variant="outline-primary"
                    onClick={triggerUpload}
                    disabled={uploading}
                    className="d-flex align-items-center gap-1"
                  >
                    {uploading ? <Loader size="sm" /> : <RefreshCw size={16} />}
                    {uploading ? "Uploading..." : "Upload"}
                  </Button>

                  <Button
                    variant="outline-success"
                    className="d-flex align-items-center gap-1"
                    onClick={() => qrCode && window.open(qrCode, "_blank")}
                  >
                    <Download size={16} />
                  </Button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default ValetLocationQrCode;
